import { useEffect, type ReactNode } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Max height of the expanded sheet. */
  maxHeightClass?: string;
  /** Extra element rendered right of the title (e.g. a count badge). */
  titleExtra?: ReactNode;
  zClass?: string;
  /** Without backdrop the page behind stays interactive (map panels). */
  backdrop?: boolean;
}

/**
 * Mobile bottom sheet: slides up from the bottom edge with a grab handle,
 * scrollable body, and 44px+ close target. With `backdrop={false}` it acts
 * as a non-modal panel so the map behind stays pannable.
 */
export default function BottomSheet({
  open,
  onClose,
  title,
  children,
  maxHeightClass = 'max-h-[62dvh]',
  titleExtra,
  zClass = 'z-[9000]',
  backdrop = false,
}: BottomSheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      {backdrop && open && (
        <div className={clsx('fixed inset-0 bg-black/50', zClass)} onClick={onClose} />
      )}
      <div
        role="dialog"
        aria-label={title}
        aria-hidden={!open}
        className={clsx(
          'fixed inset-x-0 bottom-0 flex flex-col rounded-t-2xl border-t border-x border-command-border',
          'bg-command-panel/95 backdrop-blur shadow-2xl',
          'transition-transform duration-300 ease-out will-change-transform',
          maxHeightClass,
          zClass,
          open ? 'translate-y-0' : 'translate-y-full pointer-events-none'
        )}
      >
        {/* Grab handle + header (whole row is a close target) */}
        <button
          type="button"
          onClick={onClose}
          className="flex-shrink-0 w-full pt-2 pb-1 px-4 min-h-11 touch-manipulation"
          aria-label={`Close ${title}`}
        >
          <div className="mx-auto w-10 h-1 rounded-full bg-gray-600 mb-2" />
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-100 flex items-center gap-2">
              {title}
              {titleExtra}
            </span>
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </div>
        </button>
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {children}
        </div>
      </div>
    </>
  );
}
